import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import type { ConditionField, SortField } from "../../../../../../api/types/svTypes.ts";
import { AreaItemType, Ordering, ReportType } from "../../../../../../api/types/svTypes.ts";
import { getSortType, sortSortingFields } from "../../components/builder/common/utilities/sortFieldStateHelper";
import { createListReportConfiguration } from "../../components/builder/utils/configuration/listConfiguration";
import { ensureDefaultFormatIsSet } from "../../utilities/fieldsHelper.ts";
import { getDimensionNames } from "../../utilities/getDimensionNames.ts";
import { getMeasureNames } from "../../utilities/getMeasureNames.ts";
import { createListDefaultSettings } from "../../utilities/isConfigurationValid.ts";
import { areAllFieldsEmpty, insertItemAt } from "../../utilities/Utilities.tsx";
import type { ListFieldsActionTypes, ListFieldState } from "../types/fieldsState.types";
import {
  calculateAndAssignSystemLabel,
  moveConditionItem,
  moveItem,
  moveSortingItem,
  removeConditionItem,
  removeItem,
  removeSortingItem,
  updateConditionConfig,
  updateConditionItem,
} from "../utils/fieldsState.ts";
import { updateTableFieldsOrder, validateCondition, validateCustomFields } from "../utils/fieldsUtils.ts";
import { withListDateEqualityDefault } from "../utils/listConditionsHelper.ts";
import { LIST_SLICE_NAME } from "../utils/storeUtils";

const initialState: ListFieldState = {
  conditions: [],
  fields: [],
  sorts: [],
  settings: createListDefaultSettings(),
  dimensionNames: [],
  measureNames: [],
  configuration: undefined,
};

const listFieldsSlice = createSlice({
  name: LIST_SLICE_NAME,
  initialState,
  reducers: {
    resetToDefaultAction: () => initialState,
    // CONDITIONS
    setConditions: (state, action: ListFieldsActionTypes["setConditions"]) => {
      state.conditions = action.payload;
    },
    addCondition: (state, action: ListFieldsActionTypes["addCondition"]) => {
      const condition: ConditionField = withListDateEqualityDefault({ ...action.payload.field });
      calculateAndAssignSystemLabel(condition, state.conditions);
      state.conditions = insertItemAt(state.conditions, condition, action.payload.index);
    },
    removeCondition: (state, action: ListFieldsActionTypes["removeCondition"]) => {
      state.conditions = removeConditionItem(action.payload, state.conditions);
    },
    moveCondition: (state, action: ListFieldsActionTypes["moveCondition"]) => {
      state.conditions = moveConditionItem(action.payload.field, action.payload.newIndex, state.conditions);
    },
    updateCondition: (state, action: ListFieldsActionTypes["updateCondition"]) => {
      state.conditions = updateConditionItem(action.payload.field, action.payload.changes, state.conditions);
    },
    updateConditionConfig: (state, action: ListFieldsActionTypes["updateConditionConfig"]) => {
      const condition = state.conditions.find((c) => c.config.guid === action.payload.field.config.guid);
      if (condition) {
        condition.config = updateConditionConfig(condition.config, action.payload.changes);
      }
    },
    // FIELDS
    setFields: (state, action: ListFieldsActionTypes["setFields"]) => {
      state.fields = action.payload;
    },
    addField: (state, action: ListFieldsActionTypes["addField"]) => {
      state.fields = insertItemAt(state.fields, ensureDefaultFormatIsSet(action.payload.field), action.payload.index);
    },
    removeField: (state, action: ListFieldsActionTypes["removeField"]) => {
      const sortItem = state.sorts.find((s) => s.config.id === action.payload.config.guid);
      if (sortItem) {
        state.sorts = removeSortingItem(sortItem, state.sorts);
      }
      state.settings.freezedColumns = state.settings.freezedColumns.filter(
        (column) => column !== action.payload.config.guid
      );
      state.fields = removeItem(action.payload, state.fields);
    },
    moveField: (state, action: ListFieldsActionTypes["moveField"]) => {
      state.fields = moveItem(action.payload.field, action.payload.newIndex, state.fields);
    },
    updateFieldConfig: (state, action: ListFieldsActionTypes["updateFieldConfig"]) => {
      const field = state.fields.find((f) => f.config.guid === action.payload.field.config.guid);
      if (field !== undefined) {
        field.config = { ...field.config, ...action.payload.changes };
      }
    },
    updateFieldsOrder: (state, action: ListFieldsActionTypes["updateFieldsOrder"]) => {
      state.fields = updateTableFieldsOrder(state.fields, action.payload);
    },
    setLedger: (state, action: ListFieldsActionTypes["setLedger"]) => {
      state.ledger = action.payload;
    },
    applyLedgerChange: (state, action: ListFieldsActionTypes["applyLedgerChange"]) => {
      state.ledger = action.payload.ledger;
      state.conditions = action.payload.conditions;
      state.fields = action.payload.fields;
      state.sorts = action.payload.sorts;
      state.settings = action.payload.settings;
    },
    // SORTING
    setSorting: (state, action: ListFieldsActionTypes["setSorting"]) => {
      state.sorts = sortSortingFields(action.payload.fields, []);
    },
    addSorting: (state, action: ListFieldsActionTypes["addSorting"]) => {
      const { meta, config } = action.payload;
      const sortField = {
        meta: meta,
        config: {
          name: meta.name,
          id: config.guid,
          ordering: Ordering.Ascending,
          caption: config.customLabel || meta.caption,
          isGroupField: false,
          type: getSortType(action.payload),
        },
        areaItemType: AreaItemType.SORTS,
      } satisfies SortField;
      state.sorts = sortSortingFields([...state.sorts, sortField], []);
    },
    removeSorting: (state, action: ListFieldsActionTypes["removeSorting"]) => {
      state.sorts = removeSortingItem(action.payload, state.sorts);
    },
    moveSorting: (state, action: ListFieldsActionTypes["moveSorting"]) => {
      state.sorts = moveSortingItem(action.payload.field, action.payload.newIndex, state.sorts);
    },
    updateSortingConfig: (state, action: ListFieldsActionTypes["updateSortingConfig"]) => {
      const sortField = state.sorts.find((s) => s.config.id === action.payload.field.config.id);
      if (sortField) {
        sortField.config = { ...sortField.config, ...action.payload.changes };
      }
    },
    //SETTINGS
    updateSettings: (state, action: ListFieldsActionTypes["updateSettings"]) => {
      Object.assign(state.settings, action.payload);
    },
    setSettings: (state, action: ListFieldsActionTypes["setSettings"]) => {
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        listFieldsSlice.actions.setFields,
        listFieldsSlice.actions.addField,
        listFieldsSlice.actions.removeField,
        listFieldsSlice.actions.updateFieldConfig,
        listFieldsSlice.actions.applyLedgerChange,
        listFieldsSlice.actions.setConditions,
        listFieldsSlice.actions.addCondition,
        listFieldsSlice.actions.moveCondition,
        listFieldsSlice.actions.removeCondition,
        listFieldsSlice.actions.updateConditionConfig
      ),
      (state) => {
        state.fields = validateCustomFields(state.fields, state.conditions);
      }
    );
    //This has to be the last matcher
    builder.addMatcher(isAnyOf(...Object.values(listFieldsSlice.actions)), (state) => {
      state.conditions.forEach((condition) => {
        condition.validation = validateCondition(condition, state.conditions, ReportType.List);
      });
      state.fields = validateCustomFields(state.fields, state.conditions);
      const configuration = createListReportConfiguration(
        state.conditions,
        state.fields,
        state.sorts,
        state.settings,
        state.ledger
      );
      state.configuration = configuration;
      state.isConfigurationEmpty = areAllFieldsEmpty({
        conditions: configuration.conditions,
        fields: configuration.fields,
        sort: configuration.sort,
      });
      state.dimensionNames = getDimensionNames(ReportType.List, configuration);
      state.measureNames = getMeasureNames(ReportType.List, configuration);
    });
  },
});

export const listFieldsActions = listFieldsSlice.actions;
export const listFieldsReducer = listFieldsSlice.reducer;
